/**
 * The one form a festival has: create it, correct it afterwards — and delete it.
 *
 * ---------------------------------------------------------------------------------------------
 * WHY THIS IS A DIALOG AT ALL, WITH DESIGN.md §11.3 SAYING "aldri en modal for noe som kunne
 * vært på siden".
 *
 * Because the create case has no page to be on. An organisation with no festivals has an empty
 * sidebar section and nothing else — no list screen, no detail screen, no surface at all — so
 * "put it on the page" has no page to name. The edit case could have been an inline form on the
 * festival page, and it is the same six fields with the same six validation rules; a second form
 * is a second set of rules, and the first one to drift is the one somebody uses less.
 *
 * J1's rule of 19 September keeps it here: «Forblir modal: Ny festival, Ny årgang, bekreftelser».
 *
 * VALIDATION IS THE SHARED SCHEMA, NOT A COPY OF IT. `createFestivalRequestSchema` is the very
 * schema the API validates the request with (principle 7), handed to `i18nZodResolver` so the
 * browser and the API cannot disagree about what "valid" means. Two things it does NOT cover are
 * added deliberately and separately:
 *
 *   1. THE TIMEZONE IS A LENGTH CHECK IN THE CONTRACT. `z.string().min(1).max(64)` accepts
 *      "Bananas", and every get-in on the festival would then be read in a zone that does not
 *      exist. `isKnownTimezone` refuses it here, against the browser's own IANA list. It is a
 *      state of its own rather than a resolver rule, because the resolver IS the shared schema
 *      and this rule is not in it — pretending otherwise would be the first step to a second,
 *      divergent description of a valid request.
 *   2. AN EMPTY OPTIONAL FIELD IS `null`, NOT `''`. `websiteUrl` is `z.url().nullish()`, and an
 *      empty text input hands back an empty string, which is neither a URL nor null. Left alone
 *      the resolver reports "Påkrevd" under a field nobody has to fill in. `setValueAs` turns
 *      the three optional text fields into `null` on the way in, once, rather than each of them
 *      growing an exception.
 *
 * ---------------------------------------------------------------------------------------------
 * WHAT QA-RUNDE 12 CHANGED HERE, AND WHY EACH ONE WAS A REAL FAULT.
 *
 * #16a — TIDSSONEN VAR LÅST I PRAKSIS. It was already a free-text field with a suggestion panel,
 * and that is precisely how it managed to look locked: the panel was filled on OPEN and never
 * again, so typing narrowed nothing, and every row in it was an IANA id rather than a place.
 * `TimezoneField` is the fix, and it is a component rather than four more lines here because the
 * årgang form had the identical field with the identical two faults.
 *
 * #16c — «godnokfestivalen.no» BLOKKERTE OPPRETTING. `z.url()` is right about what may be STORED;
 * it was being asked about what may be TYPED. `normalizeWebsiteUrl` runs in `setValueAs`, which
 * is before the resolver, so the contract never sees the half address it would have refused. The
 * help text that said «Hele adressen, med https:// foran» is gone: it was an instruction for a
 * rule that no longer exists.
 *
 * #16b — TYPE-HJELPETEKSTEN STO I EN SMAL KOLONNE over three lines. The form is one column now.
 * Type, Sted and Tidssone each carry a sentence under them, and a two-column grid gave every one
 * of those sentences half the width of the dialog — three lines each, which is most of the form's
 * height spent on wrapping. DESIGN.md §5 asks forms to be airier than lists; a single column is
 * what that looks like when the fields have things to say.
 *
 * #28 — FARESONEN. It is at the FOOT of the edit form, never on the create form: there is
 * nothing to delete before the first Lagre, and a red button under a form you are filling in for
 * the first time is a trap. `DangerZone` carries the argument for the typed name.
 *
 * AND THE CONFIRMATION REPLACES THIS FORM RATHER THAN OPENING OVER IT (K-7: «Modal over modal:
 * aldri. Et nytt steg åpnes inne i samme flate, eller erstatter den»). So this dialog has two
 * steps: the form, and the delete. The title, the body and the footer all change together, which
 * also gives the right Escape behaviour for free — one layer, one key, one closing.
 *
 * ---------------------------------------------------------------------------------------------
 * WHAT IS NOT IN THIS FORM, AND WHY.
 *
 *   * `slug` — `updateFestivalRequestSchema` has none, on purpose. It is minted once from the
 *     first name and never moves, because an import, a public volunteer link and the Phase 2
 *     edition clone all address the festival by it. The edit form SHOWS it instead, in the place
 *     somebody is standing when they rename the thing and unknowingly leave the slug behind —
 *     the same arrangement, and the same wording, as `DepartmentFormModal`.
 *   * `countryCode` — in the values, never on screen. The contract defaults it and every field
 *     on this form has to survive the resolver, so it rides along at its current value and can
 *     never be in a PATCH body. A country picker is a control with no reader yet: nothing in the
 *     product renders the country, and it would be the only field here that changes nothing
 *     anybody can see.
 *   * `sortOrder` — the sidebar's manual ordering. There is no drag handle to set it against,
 *     and a bare number field for "position in a menu" is a control that has to be explained
 *     before it can be used. It stays server-side until the list it orders is draggable.
 * ---------------------------------------------------------------------------------------------
 */
import {
  createFestivalRequestSchema,
  type CreateFestivalRequest,
  type FestivalDto,
  type FestivalKind,
  type UpdateFestivalRequest,
} from '@tvitvi/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { errorKey, isApiErrorWithCode } from '@/api/errors';
import { useCreateFestival, useDeleteFestival, useUpdateFestival } from '@/api/queries/festivals';
import { useApiFormErrors } from '@/components/forms/useApiFormErrors';
import { i18nZodResolver, translateValidation } from '@/components/forms/validation';
import { Button } from '@/components/ui/Button';
import { Callout } from '@/components/ui/Callout';
import { Checkbox } from '@/components/ui/Checkbox';
import { Field } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { INPUT_CLASS, TextField } from '@/components/ui/TextField';
import { useToast } from '@/components/ui/Toast';

import { DangerZone, DeleteContainerStep, namesMatch } from './DangerZone';
import { memberChoicesQueryOptions } from './member-choices';
import { MemberChooser } from './MemberChooser';
import { isKnownTimezone } from './timezones';
import { TimezoneField } from './TimezoneField';
import { normalizeWebsiteUrl } from './website-url';

/** `createFestivalRequestSchema`'s own ceilings, so a field stops where the API would. */
const MAX_NAME_LENGTH = 120;
const MAX_CITY_LENGTH = 80;
const MAX_DESCRIPTION_LENGTH = 2000;

/** An empty optional text field is an absent value, not an empty string. See the header. */
function orNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

export interface FestivalFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  /** Absent for a new festival; present to correct an existing one. */
  festival?: FestivalDto | null;
  /** Where the caller goes next — the sidebar opens the festival it just made. */
  onSaved?: (festival: FestivalDto) => void;
  /**
   * Where the caller goes after the festival is gone. The page it was on no longer resolves, so a
   * caller that renders this from inside the festival has to leave; the sidebar's copy does not.
   */
  onDeleted?: () => void;
  /**
   * Whether the reader holds `festival:delete` — the faresone's whole gate (#28).
   *
   * ------------------------------------------------------------------------------------------
   * A PROP, NOT `useActor()`, AND THE REASON IS WHERE THIS DIALOG GETS MOUNTED FROM. One of its
   * two callers is `FestivalNavSection`, which lives in the SHELL, and the shell is built to
   * never call `useMe()` — it is a `useSuspenseQuery`, so the whole application would suspend on
   * the sidebar. That file takes `me` as a prop and says so at length; `useActor()` here would
   * have reached for the same hook from two levels inside it and broken exactly that rule.
   *
   * It is still `can()` that answers — the caller asks it with the same function the API enforces
   * with — and the DEFAULT IS `false`, which is principle 2 applied to a prop: a caller that
   * forgets gets no faresone, never an unguarded one. The server refuses again regardless
   * (decision 0005: this half is UX, the other half is the security property).
   */
  canDelete?: boolean;
  /**
   * What a brand-new festival starts on. Passed in rather than read here so this form has no
   * opinion of its own about which zone or country is "normal" — the caller's proposal is.
   */
  defaultTimezone: string;
  defaultCountryCode: string;
  /**
   * HVA EN NY RAD ER, og det er knappen som sier det — ikke et felt i skjemaet (QA13 #27, Atle:
   * «Hver knapp lager sin egen ting»). «Ny festival» lager en festival, «Nytt spillested» et
   * spillested. En eksisterende rad har ikke noe typevalg (sak 239.23), og API-et avviser en annen
   * type (`kind_locked`). Utelatt: festival.
   */
  kind?: FestivalKind;
}

function defaultsFor(
  festival: FestivalDto | null,
  defaultTimezone: string,
  defaultCountryCode: string,
  kind: FestivalKind,
): CreateFestivalRequest {
  return {
    name: festival?.name ?? '',
    kind: festival?.kind ?? kind,
    city: festival?.city ?? null,
    countryCode: festival?.countryCode ?? defaultCountryCode,
    timezone: festival?.timezone ?? defaultTimezone,
    websiteUrl: festival?.websiteUrl ?? null,
    description: festival?.description ?? null,
  };
}

export function FestivalFormModal({
  open,
  onOpenChange,
  organizationId,
  festival,
  onSaved,
  onDeleted,
  canDelete: mayDelete = false,
  defaultTimezone,
  defaultCountryCode,
  kind: newKind = 'festival',
}: FestivalFormModalProps): ReactNode {
  const { t } = useTranslation();
  const { toast } = useToast();

  const existing = festival ?? null;
  const isEdit = existing !== null;
  /*
   * QA6 L8: spillestedets Innstillinger åpner det samme skjemaet, og der sto «Rediger festivalen»,
   * «Slett festival» og årganger huset ikke har (sak 229). Ordene følger det LAGREDE typen — en
   * type som endres i skjemaet er ikke lagret ennå.
   */
  /*
   * QA15-S funn 2: en NY rad har ingen lagret type, og da er det knappen som sier hva den lager
   * («Nytt spillested» sender `kind="venue"`). Før falt den tilbake på festival, og dialogen het
   * «Ny festival» med årganger i underteksten.
   */
  const isVenue = (existing?.kind ?? newKind) === 'venue';

  const create = useCreateFestival(organizationId);
  const update = useUpdateFestival(organizationId);
  const remove = useDeleteFestival(organizationId);
  const mutation = isEdit ? update : create;

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<CreateFestivalRequest>({
    resolver: i18nZodResolver(createFestivalRequestSchema),
    defaultValues: defaultsFor(existing, defaultTimezone, defaultCountryCode, newKind),
  });

  /** The one rule that is not in the shared schema. See the header. */
  const [zoneUnknown, setZoneUnknown] = useState(false);
  /**
   * Which of the two steps this dialog is showing. `'delete'` is only ever reachable from the
   * faresone at the foot of the edit form; a create never has it.
   */
  const [step, setStep] = useState<'form' | 'members' | 'delete'>('form');
  /** What has been typed into the confirmation. Held here because the footer button reads it. */
  const [typedName, setTypedName] = useState('');

  /*
   * «HVEM ER MED?» (QA13 punkt 32) — steg to i «Ny festival», aldri på et spillested og aldri på
   * en redigering. Festivalen har ingen årgang ennå, så valget lagres på festivalen og krysses av
   * på forhånd i dens første «Ny årgang». Personer i en årgang som finnes, endres på Personer.
   */
  const asksWhoIsWith = existing === null && newKind === 'festival';
  const choices = useQuery({
    ...memberChoicesQueryOptions(organizationId, 'new-festival'),
    enabled: open && asksWhoIsWith,
  });
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const choosing = asksWhoIsWith && step === 'members';

  const timezone = watch('timezone');

  /*
   * SAK 215.13: ENDRES SONEN, SPØR TVITVI OM ÅRGANGENE SOM FINNES. Hver årgang har sin egen sone
   * (den arves bare når årgangen lages), så en ny sone her rører ingen årgang med mindre svaret
   * er ja. Ukrysset er «bare nye årganger», som før spørsmålet fantes.
   */
  const [applyToEditions, setApplyToEditions] = useState(false);
  const asksAboutEditions =
    existing !== null && existing.editionCount > 0 && timezone !== existing.timezone;

  useApiFormErrors(mutation.error, setError);

  const onSubmit = handleSubmit(async (values) => {
    if (!isKnownTimezone(values.timezone)) {
      setZoneUnknown(true);
      return;
    }
    setZoneUnknown(false);
    /* Skjemaet er gyldig: videre til «Hvem er med?». Ingenting er lagret ennå. */
    if (asksWhoIsWith && step === 'form') {
      setStep('members');
      return;
    }

    try {
      let saved: FestivalDto;
      if (existing === null) {
        saved = await create
          .mutateAsync(asksWhoIsWith ? { ...values, memberPersonIds: [...chosen] } : values)
          .catch((error: unknown) => {
            /* Feilen hører til et felt i steg én (et navn som finnes), og står der. */
            setStep('form');
            throw error;
          });
      } else {
        const body = patchFrom(existing, values);
        if (asksAboutEditions && applyToEditions) body.applyTimezoneToEditions = true;
        // A PATCH that changes nothing is refused by `updateFestivalRequestSchema`, and rightly:
        // it would still be an audit row and a cache invalidation. Closing is the right answer to
        // "save" on an untouched form — the values on screen are already the saved ones.
        if (Object.keys(body).length === 0) {
          onOpenChange(false);
          return;
        }
        saved = await update.mutateAsync({ festivalId: existing.id, body });
      }

      toast({
        title: isVenue
          ? isEdit
            ? t('festivals.venueToast.updated')
            : t('festivals.venueToast.created')
          : isEdit
            ? t('festivals.toast.updated')
            : t('festivals.toast.created'),
        description: saved.name,
        tone: 'success',
      });
      setStep('form');
      setChosen(new Set());
      onOpenChange(false);
      onSaved?.(saved);
    } catch {
      // Rendered below, and on the field itself where the API named one. The toast is the fast
      // notice; it is never the record (see `Toast`).
    }
  });

  /**
   * The delete, run from the confirmation and reported from here.
   *
   * The toast carries no «Angre», and that is not an omission — see `useDeleteFestival`. The
   * dialog is what asked, and the typed name is what was paid instead.
   */
  async function onDelete(): Promise<void> {
    if (existing === null) return;
    try {
      await remove.mutateAsync(existing.id);
      toast({
        title: isVenue ? t('festivals.venueToast.deleted') : t('festivals.toast.deleted'),
        description: existing.name,
        tone: 'success',
      });
      onOpenChange(false);
      onDeleted?.();
    } catch {
      // `DeleteContainerStep` renders it, in the dialog that is still open — a failure behind a
      // dialog that closed is a delete somebody believes happened.
    }
  }

  /**
   * A live festival already holds the slug this name produces: 409 from
   * `ux_festivals__org_slug`, and it belongs on the name rather than in a general box. There is
   * no soft-deleted twin to restore here — see the header.
   */
  const duplicate = isApiErrorWithCode(mutation.error, 'ALREADY_EXISTS');
  const nameError = duplicate
    ? t('errors.code.alreadyExists')
    : translateValidation(t, errors.name?.message);

  const timezoneError = zoneUnknown
    ? t('festivals.field.timezoneUnknown')
    : translateValidation(t, errors.timezone?.message);

  const generalError = mutation.error != null && !duplicate && Object.keys(errors).length === 0;

  const submitLabel = isEdit ? t('common.actions.save') : t('common.actions.create');

  /*
   * `festival:delete` er superadmin alene i standardmatrisen, og cella er DATA — et hus kan flytte
   * den. Spørsmålet stilles av `can()` hos kalleren (se proppen), aldri av en rollesammenligning,
   * og svaret SKJULER sonen framfor å grå den ut (vedtak 0005). Og aldri på en oppretting: det
   * finnes ingenting å slette før den første Lagre.
   */
  const canDelete = isEdit && mayDelete;
  const deleting = step === 'delete' && canDelete && existing !== null;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        /* Neste gang dialogen åpnes, begynner den på steg én. */
        if (!next && step === 'members') setStep('form');
        onOpenChange(next);
      }}
      title={
        deleting && existing !== null
          ? t('festivals.danger.confirmTitle', { name: existing.name })
          : choosing
            ? t('festivals.form.whoTitle', { name: watch('name') })
            : isVenue
              ? isEdit
                ? t('festivals.venueForm.editTitle')
                : t('festivals.venueForm.createTitle')
              : isEdit
                ? t('festivals.form.editTitle')
                : t('festivals.form.createTitle')
      }
      {...(isEdit || deleting || choosing
        ? {}
        : {
            description: isVenue
              ? t('festivals.venueForm.createDescription')
              : t('festivals.form.createDescription'),
          })}
      data-testid="festival-form-modal"
      footer={
        deleting && existing !== null ? (
          <>
            {/* «Avbryt» goes BACK to the form rather than closing the dialog: the person came
                here from a form they may still want, and throwing it away is a second surprise
                under a red button. */}
            <Button
              onClick={() => {
                setTypedName('');
                setStep('form');
              }}
            >
              {t('common.actions.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              disabled={!namesMatch(typedName, existing.name)}
              onClick={() => void onDelete()}
              data-testid="festival-delete-confirm"
            >
              {isVenue ? t('festivals.venueDanger.action') : t('festivals.danger.action')}
            </Button>
          </>
        ) : choosing ? (
          <>
            <Button onClick={() => setStep('form')} data-testid="festival-form-back">
              {t('common.actions.back')}
            </Button>
            <Button
              variant="primary"
              loading={mutation.isPending}
              onClick={() => void onSubmit()}
              data-testid="festival-form-submit"
            >
              {submitLabel}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => onOpenChange(false)}>{t('common.actions.cancel')}</Button>
            {asksWhoIsWith ? (
              <Button
                variant="primary"
                onClick={() => void onSubmit()}
                data-testid="festival-form-next"
              >
                {t('common.actions.next')}
              </Button>
            ) : (
              <Button
                variant="primary"
                loading={mutation.isPending}
                onClick={() => void onSubmit()}
                data-testid="festival-form-submit"
              >
                {submitLabel}
              </Button>
            )}
          </>
        )
      }
    >
      {choosing ? (
        choices.isError ? (
          <Callout tone="danger">{t(errorKey(choices.error))}</Callout>
        ) : (
          <MemberChooser
            members={choices.data?.items ?? []}
            chosen={chosen}
            onChange={setChosen}
            data-testid="festival-members"
          />
        )
      ) : null}
      {deleting && existing !== null ? (
        <DeleteContainerStep
          intro={t('festivals.danger.confirmIntro', { name: existing.name })}
          consequences={
            isVenue
              ? [
                  t('festivals.venueDanger.takes.rooms'),
                  t('festivals.venueDanger.takes.tasks'),
                  t('festivals.venueDanger.takes.documents'),
                ]
              : [
                  t('festivals.danger.takes.editions', { count: existing.editionCount }),
                  t('festivals.danger.takes.bookings'),
                  t('festivals.danger.takes.tasks'),
                  t('festivals.danger.takes.documents'),
                ]
          }
          requiredName={existing.name}
          typed={typedName}
          onTypedChange={setTypedName}
          error={remove.error}
          data-testid="festival-delete-step"
          inputTestId="festival-delete-name"
        />
      ) : null}

      {/*
        HIDDEN RATHER THAN UNMOUNTED WHILE THE DELETE STEP IS UP. `register` keeps a value in the
        DOM node, and «Avbryt» on the confirmation is supposed to hand back the form as it was —
        half-typed name and all. Rebuilding it from `defaultValues` would silently discard
        somebody's edit as the price of having read a warning and changed their mind.
      */}
      <div hidden={deleting || choosing}>
        {/*
        ONE COLUMN (#16b). Three of the six fields carry a sentence underneath them, and a
        two-column grid gave each of those sentences half a dialog to wrap in — Atle counted three
        lines under Type alone. See the header.
      */}
        <form
          className="flex flex-col gap-3"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void onSubmit();
          }}
        >
          {generalError ? <Callout tone="danger">{t(errorKey(mutation.error))}</Callout> : null}

          <Field label={t('festivals.field.name')} error={nameError}>
            {(control) => (
              <TextField
                {...control}
                {...register('name')}
                maxLength={MAX_NAME_LENGTH}
                autoComplete="off"
                data-testid="festival-name"
              />
            )}
          </Field>

          {existing === null ? null : (
            /*
            The slug, and why it did not follow the name. Deliberately not a `Field`: there is
            nothing here to change, and a read-only input invites somebody to try.
          */
            <div
              className="flex flex-col gap-1 rounded-[var(--radius-inner)] bg-[var(--fill-tonal)] px-3 py-2"
              data-testid="festival-form-slug"
            >
              <p className="text-sm text-default">
                {t('festivals.slugLabel', { slug: existing.slug })}
              </p>
              <p className="max-w-prose text-sm leading-snug text-muted">
                {t('festivals.slugHelp')}
              </p>
            </div>
          )}

          {/*
          «STED», IKKE «BY» (#16b). Column and contract still say `city`, because that is what the
          database holds and code is English — the word that changed is the one on screen. Steigen
          is a municipality and Roskilde is a town; «Sted» is true of both, and it is also true of
          the gravel pit an outdoor festival names as its address.
        */}
          <Field
            label={t('festivals.field.city')}
            optionalLabel={t('common.optional')}
            error={translateValidation(t, errors.city?.message)}
          >
            {(control) => (
              <TextField
                {...control}
                {...register('city', { setValueAs: orNull })}
                maxLength={MAX_CITY_LENGTH}
                autoComplete="address-level2"
                data-testid="festival-city"
              />
            )}
          </Field>

          <Field
            label={t('festivals.field.timezone')}
            description={
              isVenue ? t('festivals.field.venueTimezoneHelp') : t('festivals.field.timezoneHelp')
            }
            error={timezoneError}
          >
            {(control) => (
              <TimezoneField
                {...control}
                value={timezone}
                onChange={(next) => {
                  setValue('timezone', next, { shouldDirty: true, shouldValidate: true });
                  setZoneUnknown(false);
                }}
                name="timezone"
                data-testid="festival-timezone"
              />
            )}
          </Field>

          {asksAboutEditions ? (
            <Callout tone="info" data-testid="festival-timezone-editions">
              <div className="flex flex-col gap-2">
                <span>
                  {isVenue
                    ? t('festivals.field.venueTimezoneEditionsQuestion')
                    : t('festivals.field.timezoneEditionsQuestion', {
                        count: existing.editionCount,
                      })}
                </span>
                <Checkbox
                  label={
                    isVenue
                      ? t('festivals.field.venueTimezoneEditionsApply')
                      : t('festivals.field.timezoneEditionsApply', {
                          count: existing.editionCount,
                        })
                  }
                  checked={applyToEditions}
                  onChange={(event) => setApplyToEditions(event.target.checked)}
                  data-testid="festival-timezone-editions-apply"
                />
              </div>
            </Callout>
          ) : null}

          {/*
          NO HELP TEXT UNDER THE ADDRESS ANY MORE (#16c). It said «Hele adressen, med https://
          foran» — an instruction for a rule this form no longer has. `normalizeWebsiteUrl` puts
          the scheme on, so the sentence would now be telling people to do work the field does.
        */}
          <Field
            label={t('festivals.field.website')}
            optionalLabel={t('common.optional')}
            error={translateValidation(t, errors.websiteUrl?.message)}
          >
            {(control) => (
              <TextField
                {...control}
                {...register('websiteUrl', { setValueAs: normalizeWebsiteUrl })}
                /*
                `type="text"`, NOT `type="url"`. A `url` input makes the BROWSER refuse
                «godnokfestivalen.no» with its own bubble, before any of our code runs — which is
                the very complaint #16c is about, arriving from a second direction. `inputMode`
                still gives a phone the right keyboard.
              */
                type="text"
                inputMode="url"
                autoComplete="url"
                data-testid="festival-website"
              />
            )}
          </Field>

          <Field
            label={t('festivals.field.description')}
            optionalLabel={t('common.optional')}
            error={translateValidation(t, errors.description?.message)}
          >
            {(control) => (
              <textarea
                {...control}
                {...register('description', { setValueAs: orNull })}
                rows={4}
                maxLength={MAX_DESCRIPTION_LENGTH}
                className={INPUT_CLASS}
                data-testid="festival-description"
              />
            )}
          </Field>

          {/* Enter submits, the way it does in every other Tvitvi form. `aria-hidden` because
            `sr-only` hides it from the eye and not from the accessible tree, and a dialog with
            two buttons called "Lagre" is a dialog a screen reader describes wrongly. */}
          <button type="submit" aria-hidden className="sr-only" tabIndex={-1}>
            {submitLabel}
          </button>
        </form>

        {canDelete ? (
          <DangerZone
            description={
              isVenue ? t('festivals.venueDanger.description') : t('festivals.danger.description')
            }
            actionLabel={isVenue ? t('festivals.venueDanger.action') : t('festivals.danger.action')}
            onAction={() => setStep('delete')}
            data-testid="festival-danger-zone"
            actionTestId="festival-delete"
          />
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Only what moved.
 *
 * `updateFestivalRequestSchema` refuses a PATCH that changes nothing, and rightly: a request
 * nobody meant to send is still an audit row and a cache invalidation. `countryCode` is compared
 * like everything else and therefore never appears — it has no control, so it cannot have moved.
 *
 * THE ADDRESS IS COMPARED NORMALISED ON BOTH SIDES (#16c). The form's value has been through
 * `normalizeWebsiteUrl`; the stored one was written before that function existed. Comparing the
 * raw stored string against a normalised new one would send a PATCH every time somebody opened
 * this dialog and pressed Lagre without touching the field.
 */
function patchFrom(existing: FestivalDto, values: CreateFestivalRequest): UpdateFestivalRequest {
  const body: UpdateFestivalRequest = {};
  if (values.name !== existing.name) body.name = values.name;
  if ((values.city ?? null) !== existing.city) body.city = values.city ?? null;
  if (values.countryCode !== existing.countryCode) body.countryCode = values.countryCode;
  if (values.timezone !== existing.timezone) body.timezone = values.timezone;
  if ((values.websiteUrl ?? null) !== normalizeWebsiteUrl(existing.websiteUrl)) {
    body.websiteUrl = values.websiteUrl ?? null;
  }
  if ((values.description ?? null) !== existing.description) {
    body.description = values.description ?? null;
  }
  return body;
}
